// Maps a saved filer profile onto a form's fields using label/name heuristics.
// Only returns values for fields that are currently empty (won't overwrite).
import { humanizeLabel } from "@/lib/humanize";

const has = (hay, needles) => needles.some((n) => hay.includes(n));
// Humanize (adds spaces to camelCase) so word-boundary matching is reliable,
// e.g. "LawFirmName" -> "law firm name", "SwornAffirmedDate" -> "sworn affirmed date".
const hkey = (f) => `${humanizeLabel(f.name || "")} ${humanizeLabel(f.label || "")}`.toLowerCase();
const FIRM_RE = /\bfirm\b|law office|law firm/;   // avoids "affirmed"/"confirm"

export function mapProfileToFields(fields, profile, currentValues = {}) {
  if (!profile) return {};
  const out = {};
  for (const f of fields) {
    if (f.type === "checkbox" || f.type === "select") continue;
    const cur = currentValues[f.name];
    if (cur !== undefined && cur !== null && String(cur).trim() !== "") continue; // don't overwrite
    const key = hkey(f);
    let val = "";

    if (/registered\s*agent/.test(key)) val = profile.registered_agent;
    else if (/(entity|corporation|company|business|organization)\s*name|name of (the )?(entity|business|corporation|company|organization|limited liability company|llc)/.test(key)) val = profile.entity_name;
    else if (/bar\s*(no|number)|state\s*bar|attorney\s*bar/.test(key)) val = profile.bar_number;
    else if (/\bsuite\b|\bunit\b/.test(key)) val = profile.suite;
    else if (has(key, ["attorney", "counsel"]) && has(key, ["name"])) val = profile.attorney_name;
    else if (FIRM_RE.test(key)) val = profile.firm;
    else if (has(key, ["county"]) && !has(key, ["birthplace"])) val = profile.county;
    else if (has(key, ["file no", "file number", "filenumber", "case no", "case number", "casenumber", "docket"])) val = profile.case_number;
    else if (has(key, ["zip", "postal"])) val = profile.zip_code;
    else if (has(key, ["email", "e-mail"])) val = profile.email;
    else if (has(key, ["phone", "telephone", "tel."])) val = profile.phone;
    else if (has(key, ["address"]) && !has(key, ["email", "e-mail", "web"])) val = profile.address_line;
    else if (has(key, ["city"]) && !has(key, ["capacity"])) val = profile.city;
    else if ((key.trim() === "state" || has(key, [" state", "state of"])) && !has(key, ["statement", "birthplace", "united states", "estate"])) val = profile.state;
    else if (has(key, ["name"]) && has(key, ["print", "signature", "type or print", "your name", "filer", "affiant", "petitioner", "plaintiff"])) val = profile.full_name;

    if (val && String(val).trim()) out[f.name] = val;
  }
  return out;
}

const ATTY_CTX = /attorney|atty|counsel/;
const BAR_RE = /bar\s*(no|number)|state\s*bar|attorney\s*bar/;

// Fills ONLY attorney-context fields (name, bar, firm, office address) — never the party's fields.
export function mapAttorneyToFields(fields, profile, currentValues = {}) {
  if (!profile) return {};
  const out = {};
  for (const f of fields) {
    if (f.type === "checkbox" || f.type === "select") continue;
    const cur = currentValues[f.name];
    if (cur !== undefined && cur !== null && String(cur).trim() !== "") continue;
    const key = hkey(f);
    const isFirm = FIRM_RE.test(key);
    if (!isFirm && !ATTY_CTX.test(key) && !BAR_RE.test(key)) continue;
    let val = "";
    if (BAR_RE.test(key)) val = profile.bar_number;
    // Suite/unit number (e.g. the renamed "Plaintiff Attorney Suite Number" field).
    else if (/\bsuite\b|\bunit\b/.test(key)) val = profile.suite;
    // Address parts win over the firm/name so "Law Firm Street Address" gets the address, not the firm name.
    else if (/\bzip\b|postal/.test(key)) val = profile.zip_code;
    else if (/address|street/.test(key)) val = profile.address_line;
    else if (/\bcity\b/.test(key)) val = profile.city;
    else if (/\bstate\b|state of/.test(key) && !/statement|united states|estate/.test(key)) val = profile.state;
    else if (/e-?mail/.test(key)) val = profile.email;
    else if (/phone|telephone|\bfax\b|mobile|cell/.test(key)) val = profile.phone;
    else if (isFirm) val = profile.firm;
    else val = profile.attorney_name || profile.full_name; // any remaining attorney field → the attorney's name
    if (val && String(val).trim()) out[f.name] = val;
  }
  return out;
}

export function isProfileEmpty(profile) {
  if (!profile) return true;
  return !Object.entries(profile).some(([k, v]) => k !== "state" && k !== "updated_at" && String(v || "").trim());
}
